import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, BrainCircuit, SlidersHorizontal, FileSearch, Briefcase,
  Lightbulb, ShieldCheck, ScanLine, CheckCircle2, Sparkles, Bell,
  Mic, BarChart2, Clock, AlertTriangle, Mail, MapPin,
  ChevronRight, Building2, ArrowRight, Linkedin, Rocket, Check,
} from "lucide-react";
import { LogoCandidateDark } from "../components/Logo";
import { FEATURE_LINKS } from "./features/links";
import VideoEmbed from "../components/VideoEmbed";
import { AUTOPILOT_VIDEO_ID } from "../lib/video";
import { SOLUTION_LINKS } from "./solutions/links";

// ─── Data ────────────────────────────────────────────────────────────────────

const MODULES = [
  // Autopilot leads. It did not appear here at all until 2026-10-25 — the grid opened on the
  // Resume Analyzer, which is the one thing on this page a dozen free tools also do, while the
  // feature nothing else does was absent from the section that literally ranks the product.
  //
  // The headline above this grid has always said "Four stages". There were three entries: the
  // fourth was Job Alerts, removed 2026-10-03, and the copy was never updated — so the page
  // promised four and showed three to every visitor for three weeks.
  {
    n: "01", tag: "AUTOPILOT", title: "Apply: without the evenings",
    desc: "Autopilot finds roles that fit, rewrites your resume for each one, and fills in the employer's form. You read it and approve — nothing is sent without you.",
    icon: Rocket, accent: "#EA580C", soft: "bg-[#EA580C]/10", ink: "text-[#EA580C]",
    link: "/features/autopilot",
  },
  {
    n: "02", tag: "RESUME ANALYZER", title: "Diagnose: why you're invisible",
    desc: "Score any resume against any job yourself. The exact keywords filtering you out, a formatting check, and an AI rewrite that scores higher.",
    icon: FileSearch, accent: "#7C3AED", soft: "bg-[#7C3AED]/10", ink: "text-[#7C3AED]",
    link: "/features/resume-analyzer",
  },
  {
    n: "03", tag: "CUSTOM INTERVIEW", title: "Prepare: for that exact job",
    desc: "Upload the real JD or company docs. AI generates the questions you're likely to face and grades you strictly against your exact material.",
    icon: SlidersHorizontal, accent: "#0F172A", soft: "bg-[#0F172A]/8", ink: "text-[#0F172A]",
    link: "/features/custom-interview",
  },
  {
    n: "04", tag: "MOCK INTERVIEWS", title: "Perform: under real conditions",
    desc: "Full HR, Technical, and Behavioral rounds with role-based question banks. Every answer scored and coached — content and delivery.",
    icon: BrainCircuit, accent: "#0025D8", soft: "bg-[#0025D8]/10", ink: "text-[#0025D8]",
    link: "/features/mock-interview",
  },
];

const STATS = [
  // "Daily · AI-matched jobs to your inbox" sat here until 2026-10-25 — that was Job Alerts,
  // removed on 2026-10-03. The page was advertising a feature that no longer exists.
  { value: "~1 min", label: "From matched job to submitted application" },
  { value: "Every job", label: "Resume rewritten for that posting" },
  { value: "You approve", label: "Nothing is sent without your say-so" },
  { value: "0–100", label: "ATS match score before you apply" },
];
// ─── Section: Hero ────────────────────────────────────────────────────────────

function Hero() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-white via-white to-[#F4F7FB]">
      {/* soft brand orb */}
      <div
        className="pointer-events-none absolute -top-32 -right-32 w-[42rem] h-[42rem] rounded-full opacity-60"
        style={{ background: "radial-gradient(circle, rgba(0,37,216,0.10) 0%, rgba(124,58,237,0.06) 40%, transparent 70%)" }}
      />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

        {/* Left */}
        <div>
          <div className="inline-flex items-center gap-2 bg-white border border-[#0025D8]/15 shadow-sm px-3.5 py-1.5 rounded-full mb-7">
            <Sparkles className="w-3.5 h-3.5 text-[#0025D8]" />
            <span className="font-mono text-[11px] text-[#0025D8] font-semibold tracking-wider">FOR JOB SEEKERS · FREE TO START</span>
          </div>

          {/* Leads with Autopilot since 2026-10-25. The previous headline sold DIAGNOSIS ("Know
                why you're not getting hired") and the CTA offered a free ATS score — which a dozen
                free tools also offer — while the one capability competitors cannot cheaply copy
                went unmentioned on the first screen. Diagnosis is still here, as the reason
                Autopilot works rather than as the product itself. */}
            <h1 className="font-display font-black text-5xl md:text-[4.2rem] tracking-tight leading-[1.04] mb-6 text-[#0F172A]">
            Apply to every job that fits.<br />
            <span className="bg-gradient-to-r from-[#EA580C] to-[#7C3AED] bg-clip-text text-transparent">Without the evenings.</span>
          </h1>

          <p className="text-lg text-muted-foreground leading-relaxed mb-9 max-w-lg">
            Tailoring your resume to every posting is what works — and what nobody has time for. Autopilot finds the roles that fit, rewrites your resume for each one, and fills in the application. You read it and approve; nothing is sent without you.
          </p>

          <div className="flex gap-3 flex-wrap mb-9">
            <Link
              to="/signup?role=candidate"
              className="group inline-flex items-center gap-2 bg-[#0025D8] hover:bg-[#001CB3] text-white px-7 py-4 rounded-xl font-semibold text-sm shadow-lg shadow-[#0025D8]/20 transition-all hover:shadow-xl hover:shadow-[#0025D8]/25"
              data-testid="cta-candidate"
            >
              Start applying free
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <a
              href="#modules"
              className="inline-flex items-center gap-2 bg-white border border-border text-foreground px-6 py-4 rounded-xl font-semibold text-sm hover:bg-secondary transition-colors"
            >
              See how it works
            </a>
          </div>

          <div className="flex gap-6 flex-wrap pt-6 border-t border-border">
            {["No credit card required", "Free forever plan", "You approve every application"].map((x) => (
              <div key={x} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <CheckCircle2 className="w-3.5 h-3.5 text-[#0025D8]" /> {x}
              </div>
            ))}
          </div>
        </div>

        {/* Right — applications waiting for approval.
            Was a live mock-interview card until 2026-10-25. With the headline now about applying,
            a picture of an interview was the wrong proof, and the first screen is where a mismatch
            costs most. This shows the OUTCOME (real roles, already prepared) and answers the
            objection that stops people using auto-apply — "will it send things in my name?" — in
            the same glance, because every row is visibly waiting on the candidate. */}
        <div className="relative">
          <div className="absolute -inset-4 bg-gradient-to-tr from-[#EA580C]/10 to-[#7C3AED]/10 rounded-3xl blur-2xl" />
          <div className="relative rounded-2xl border border-border bg-white shadow-2xl shadow-[#0F172A]/10 overflow-hidden">
            <div className="flex items-center justify-between border-b border-border px-4 py-3 bg-secondary/40">
              <span className="font-mono text-[10px] text-muted-foreground tracking-[0.15em]">AUTOPILOT · READY FOR YOU</span>
              <span className="flex items-center gap-1.5 font-mono text-[10px] text-[#EA580C]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#EA580C] inline-block animate-pulse" /> 3 WAITING
              </span>
            </div>

            <div className="p-4 space-y-2.5">
              {[
                { role: "Senior QA Automation Lead", co: "Shopify", score: "95%", note: "Resume tailored · 12 questions answered" },
                { role: "SDET Manager", co: "Epic Games", score: "91%", note: "Resume tailored · 8 questions answered" },
                { role: "QA Lead, Test Automation", co: "Citi", score: "88%", note: "Resume tailored · 6 questions answered" },
              ].map((j) => (
                <div key={j.role} className="flex items-center gap-3 rounded-xl border border-border p-3">
                  <span className="w-8 h-8 rounded-lg bg-[#EA580C]/10 text-[#EA580C] grid place-items-center flex-shrink-0">
                    <Check className="w-4 h-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-[13px] text-[#0F172A] truncate">{j.role}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{j.co} · {j.note}</div>
                  </div>
                  <span className="font-mono text-[11px] font-bold text-[#EA580C] flex-shrink-0">{j.score}</span>
                </div>
              ))}

              <div className="flex items-center justify-between pt-1.5">
                <span className="inline-flex items-center gap-1.5 font-mono text-[10px] text-muted-foreground">
                  <ShieldCheck className="w-3 h-3 text-[#EA580C]" /> Nothing sent until you approve
                </span>
                <span className="inline-flex items-center gap-1 rounded-lg bg-[#EA580C] text-white px-3 py-1.5 text-[11px] font-bold">
                  Review &amp; approve
                </span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}

// ─── Section: Stats strip ─────────────────────────────────────────────────────

function StatsStrip() {
  return (
    <div className="border-y border-border bg-white">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
        {STATS.map((s) => (
          <div key={s.label} className="text-center">
            <div className="font-display font-black text-2xl md:text-3xl text-[#0F172A] tracking-tight">{s.value}</div>
            <div className="text-xs text-muted-foreground mt-1">{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Section: Modules ─────────────────────────────────────────────────────────

function Modules() {
  return (
    <section id="modules" className="max-w-7xl mx-auto px-6 md:px-12 py-24">
      <div className="text-center max-w-2xl mx-auto mb-14">
        <p className="font-mono text-[10px] text-[#0025D8] tracking-[0.2em] uppercase mb-3">How it works</p>
        <h2 className="font-display font-black text-4xl md:text-5xl tracking-tight text-[#0F172A] leading-tight">
          Get the applications out. <span className="text-[#EA580C]">Then win the interview.</span>
        </h2>
        <p className="text-muted-foreground mt-4 text-base leading-relaxed">
          Autopilot handles the applying. The rest is there for when it works — so you walk in knowing the job, the questions you'll face, and how you come across answering them.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {MODULES.map((m) => (
          <Link
            key={m.n}
            to={m.link}
            className="group relative rounded-2xl border border-border bg-white p-7 hover:border-[#0025D8]/30 hover:shadow-xl hover:shadow-[#0F172A]/5 hover:-translate-y-1 transition-all block"
          >
            <div className="flex items-start justify-between mb-6">
              <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${m.soft}`}>
                <m.icon className={`w-6 h-6 ${m.ink}`} />
              </div>
              <span className="font-mono text-[10px] text-muted-foreground">{m.n}</span>
            </div>
            <div className={`font-mono text-[10px] tracking-[0.18em] mb-2 font-bold ${m.ink}`}>{m.tag}</div>
            <h3 className="font-display font-bold text-lg mb-2 text-[#0F172A] group-hover:text-[#0025D8] transition-colors">{m.title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{m.desc}</p>
            <div className={`mt-5 flex items-center gap-1 text-xs font-semibold ${m.ink} opacity-0 group-hover:opacity-100 transition-opacity`}>
              Learn more <ChevronRight className="w-3.5 h-3.5" />
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

// ─── Section: Deep dive 1 — Mock + AI Coaching ───────────────────────────────

function AiCoaching() {
  return (
    <section className="bg-[#F4F7FB] border-y border-border py-24">
      <div className="max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

        <div>
          <p className="font-mono text-[10px] text-[#7C3AED] tracking-[0.2em] uppercase mb-4">Mock Interviews · AI Coaching</p>
          <h2 className="font-display font-black text-4xl md:text-[2.75rem] tracking-tight leading-[1.1] mb-5 text-[#0F172A]">
            Don't just get graded.<br />Get <span className="text-[#7C3AED]">coached.</span>
          </h2>
          <p className="text-muted-foreground leading-relaxed text-base mb-7">
            Run full HR, Technical, and Behavioral rounds — then after every answer, tap <strong className="text-foreground">"Coach me on this"</strong> for personalised feedback: what you got right, what to improve, and exactly how to say it better next time.
          </p>
          {[
            "Strengths + gaps on every single answer",
            "Grounded strictly in the role's reference material",
            "Coaching tips saved so you can review anytime",
          ].map((x) => (
            <div key={x} className="flex items-center gap-2.5 mb-3 text-sm text-muted-foreground">
              <CheckCircle2 className="w-4 h-4 text-[#7C3AED] flex-shrink-0" /> {x}
            </div>
          ))}
          <Link to="/interviews/new" className="inline-flex items-center gap-2 mt-5 bg-[#7C3AED] hover:bg-[#6D28D9] text-white px-6 py-3.5 rounded-xl font-semibold text-sm shadow-lg shadow-[#7C3AED]/20 transition-all">
            Start a mock interview <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="rounded-2xl border border-[#DDD6FE] bg-white shadow-xl shadow-[#7C3AED]/5 overflow-hidden">
          <div className="border-b border-[#EDE9FE] px-4 py-3 bg-[#F5F3FF] flex items-center gap-2">
            <Lightbulb className="w-3.5 h-3.5 text-[#7C3AED]" />
            <span className="font-mono text-[10px] text-[#7C3AED] tracking-[0.15em] font-bold">AI COACHING PREVIEW</span>
          </div>
          <div className="p-5">
            <div className="font-mono text-[10px] text-muted-foreground mb-1.5">Q3 · BEHAVIORAL ROUND</div>
            <p className="font-semibold text-sm mb-3 leading-snug text-[#0F172A]">Tell me about a time you led a team through a difficult project.</p>
            <div className="bg-secondary/40 border border-border rounded-xl p-3.5 text-xs text-muted-foreground mb-3 leading-relaxed italic">
              "I managed a cross-functional team to deliver a product redesign in Q3. We had tight deadlines but I kept alignment with weekly standups."
            </div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#7C3AED] text-white text-xs font-bold mb-4">
              <Lightbulb className="w-3 h-3" /> Coach me on this
            </div>
            <div className="border border-[#EDE9FE] rounded-xl p-4 space-y-3 bg-[#FAFAFF]">
              <div>
                <div className="font-mono text-[10px] font-bold text-[#10B981] flex items-center gap-1.5 mb-1.5 tracking-[0.1em]">
                  <CheckCircle2 className="w-3 h-3" /> WHAT YOU DID WELL
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">Showed clear ownership. Mentioned a concrete communication cadence.</p>
              </div>
              <div>
                <div className="font-mono text-[10px] font-bold text-[#7C3AED] mb-1.5 tracking-[0.1em]">→ HOW TO IMPROVE</div>
                <p className="text-xs text-muted-foreground leading-relaxed">Add the specific challenge, how you resolved conflict, and a measurable result to show real impact.</p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}

// ─── Section: Deep dive 2 — ATS Resume Analyzer (dark spotlight) ──────────────

// ─── Section: Autopilot ───────────────────────────────────────────────────────
// The flagship, and until 2026-10-25 it appeared on this page only as a greyed-out "Auto Apply —
// Coming soon" tile in the feature grid below. It has been live for weeks.
//
// Video-led on purpose: Autopilot asks for something unusual — permission to apply to jobs on
// someone's behalf — and the objection that stops people is "what will it do without me?". Forty
// seconds of watching an application be prepared and then WAIT for approval answers that better
// than any paragraph. Light background, so it reads as a step forward from the dark sections
// around it rather than more of the same.
function Autopilot() {
  return (
    <section className="relative overflow-hidden bg-white py-24">
      <div
        className="pointer-events-none absolute -top-32 -right-32 w-[34rem] h-[34rem] rounded-full"
        style={{ background: "radial-gradient(circle, rgba(234,88,12,0.10) 0%, transparent 70%)" }}
      />
      <div className="relative max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        <div>
          <p className="font-mono text-[10px] text-[#EA580C] tracking-[0.2em] uppercase mb-4">Autopilot</p>
          <h2 className="font-display font-black text-4xl md:text-[2.75rem] tracking-tight leading-[1.1] mb-5 text-[#0F172A]">
            Stop rewriting your resume<br />for every job.
          </h2>
          <p className="text-[#0F172A]/60 leading-relaxed text-base mb-8">
            Everyone says tailor your resume to each posting. Almost nobody does, because it takes
            twenty minutes a job. Autopilot finds roles that fit, rewrites your resume for each one,
            fills in the employer&apos;s form — and waits for your approval before anything is sent.
          </p>
          <div className="grid grid-cols-2 gap-3 mb-8">
            {[
              { label: "RESUME", value: "Tailored per job" },
              { label: "SKILL GAPS", value: "Named upfront" },
              { label: "PER APPLICATION", value: "About a minute" },
              { label: "BEFORE SENDING", value: "You approve" },
            ].map((s) => (
              <div key={s.label} className="rounded-xl bg-[#0F172A]/[0.03] border border-border p-4">
                <div className="font-mono text-[10px] text-[#0F172A]/40 tracking-[0.15em] mb-1">{s.label}</div>
                <div className="font-display font-bold text-sm text-[#0F172A]">{s.value}</div>
              </div>
            ))}
          </div>
          <Link to="/features/autopilot" className="inline-flex items-center gap-2 bg-[#EA580C] hover:bg-[#C2410C] text-white px-6 py-3.5 rounded-xl font-semibold text-sm transition-colors">
            See how Autopilot works <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        <VideoEmbed id={AUTOPILOT_VIDEO_ID} title="How VetraHub Autopilot works" />
      </div>
    </section>
  );
}

function ResumeAnalyzer() {
  return (
    <section className="relative overflow-hidden bg-[#0F172A] text-white py-24">
      <div
        className="pointer-events-none absolute -bottom-40 -left-40 w-[40rem] h-[40rem] rounded-full"
        style={{ background: "radial-gradient(circle, rgba(124,58,237,0.18) 0%, rgba(0,37,216,0.10) 45%, transparent 70%)" }}
      />
      <div className="relative max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

        <div>
          <p className="font-mono text-[10px] text-white/40 tracking-[0.2em] uppercase mb-4">Resume Analyzer</p>
          <h2 className="font-display font-black text-4xl md:text-[2.75rem] tracking-tight leading-[1.1] mb-5">
            Know your ATS score<br />before the recruiter does.
          </h2>
          <p className="text-white/55 leading-relaxed text-base mb-8">
            The average corporate role gets 250+ applications and the ATS rejects most before a human looks. Ours shows you your score, your missing keywords, and whether your formatting survives — then rewrites your resume to pass.
          </p>
          <div className="grid grid-cols-2 gap-3 mb-8">
            {[
              { label: "ATS SCORE", value: "0 – 100" },
              { label: "KEYWORD MATCH", value: "Exact gaps" },
              { label: "FORMATTING", value: "Pass / Fail" },
              { label: "AI REWRITE", value: "Instant" },
            ].map((s) => (
              <div key={s.label} className="rounded-xl bg-white/5 border border-white/10 p-4">
                <div className="font-mono text-[10px] text-white/35 tracking-[0.15em] mb-1">{s.label}</div>
                <div className="font-display font-bold text-sm">{s.value}</div>
              </div>
            ))}
          </div>
          <Link to="/resume-analyzer" className="inline-flex items-center gap-2 bg-white text-[#0F172A] hover:bg-white/90 px-6 py-3.5 rounded-xl font-semibold text-sm transition-colors">
            Analyse my resume <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="rounded-2xl border border-white/10 p-5 bg-white/[0.03] backdrop-blur-sm shadow-2xl">
          <div className="font-mono text-[10px] text-white/35 tracking-[0.15em] mb-4">ATS SIMULATION RESULTS</div>
          <div className="grid grid-cols-3 gap-3 mb-5">
            {[
              { label: "ATS SCORE", value: "74", color: "text-[#F59E0B]" },
              { label: "KEYWORD MATCH", value: "61%", color: "text-[#F59E0B]" },
              { label: "FORMATTING", value: "PASS", color: "text-[#10B981]" },
            ].map((s) => (
              <div key={s.label} className="rounded-xl bg-white/5 border border-white/10 p-4 text-center">
                <div className="font-mono text-[9px] text-white/30 mb-1.5">{s.label}</div>
                <div className={`font-display font-black text-2xl ${s.color}`}>{s.value}</div>
              </div>
            ))}
          </div>

          <div className="mb-5">
            <div className="font-mono text-[10px] text-[#F87171] tracking-[0.15em] mb-2">KEYWORDS MISSING</div>
            <div className="flex flex-wrap gap-1.5">
              {["Docker", "CI/CD", "TypeScript", "REST APIs", "Agile"].map((k) => (
                <span key={k} className="px-2.5 py-1 text-xs font-mono rounded-lg border border-[#F87171]/30 bg-[#F87171]/8 text-[#F87171]">{k}</span>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between rounded-xl border border-[#7C3AED]/30 bg-[#7C3AED]/10 p-3.5">
            <div className="flex items-center gap-2 text-sm font-bold text-[#A78BFA]">
              <Sparkles className="w-3.5 h-3.5" /> Optimise for ATS
            </div>
            <div className="font-mono text-xs text-white/40">74 → 91</div>
          </div>
        </div>

      </div>
    </section>
  );
}

function DeliveryAndIntegrity() {
  return (
    <section className="max-w-6xl mx-auto px-6 md:px-12 py-24">
      <div className="text-center max-w-2xl mx-auto mb-14">
        <p className="font-mono text-[10px] text-[#0025D8] tracking-[0.2em] uppercase mb-3">Beyond the answer</p>
        <h2 className="font-display font-black text-4xl md:text-5xl tracking-tight text-[#0F172A] leading-tight">
          We score <span className="text-[#0025D8]">how</span> you show up, too.
        </h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Speech Delivery */}
        <div className="rounded-2xl border border-border bg-white p-8 shadow-sm hover:shadow-xl hover:shadow-[#0F172A]/5 transition-shadow">
          <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0025D8]/10 mb-5">
            <Mic className="w-6 h-6 text-[#0025D8]" />
          </div>
          <h3 className="font-display font-bold text-xl text-[#0F172A] mb-2">Speech delivery analysis</h3>
          <p className="text-sm text-muted-foreground leading-relaxed mb-6">
            Your words matter — so does how you say them. Every answer is analysed for pace, filler words, pauses, and weak language, scored alongside your content.
          </p>
          <div className="grid grid-cols-2 gap-3 mb-5">
            {[
              { icon: Mic, label: "Speaking pace", value: "142 WPM", color: "text-[#10B981]" },
              { icon: AlertTriangle, label: "Filler words", value: "6", color: "text-[#F59E0B]" },
              { icon: Clock, label: "Longest pause", value: "4.2s", color: "text-[#F59E0B]" },
              { icon: BarChart2, label: "Weak language", value: "3 flags", color: "text-[#E11D48]" },
            ].map(({ icon: Icon, label, value, color }) => (
              <div key={label} className="rounded-xl bg-secondary/40 border border-border p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="font-mono text-[9px] text-muted-foreground tracking-[0.1em] uppercase">{label}</span>
                </div>
                <div className={`font-display font-bold text-lg ${color}`}>{value}</div>
              </div>
            ))}
          </div>
          <div className="rounded-xl bg-[#F59E0B]/8 border border-[#F59E0B]/20 p-3.5">
            <div className="font-mono text-[10px] text-[#B45309] tracking-[0.12em] mb-1">TOP TIP</div>
            <p className="text-xs text-muted-foreground leading-relaxed">Replace filler words with a deliberate 1-second pause. Silence reads as confidence.</p>
          </div>
        </div>

        {/* Integrity Layer */}
        <div className="rounded-2xl border border-border bg-[#0F172A] text-white p-8 shadow-sm overflow-hidden relative">
          <div
            className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full"
            style={{ background: "radial-gradient(circle, rgba(225,29,72,0.15) 0%, transparent 70%)" }}
          />
          <div className="relative">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#E11D48]/15 mb-5">
              <ShieldCheck className="w-6 h-6 text-[#FB7185]" />
            </div>
            <h3 className="font-display font-bold text-xl mb-2">Practice under real interview pressure</h3>
            <p className="text-sm text-white/55 leading-relaxed mb-6">
              Real interviews are on camera, and drifting eyes read as nerves. Our on-device focus tracker shows you when your attention wanders so you can train it out — no video ever leaves your browser.
            </p>

            <div className="relative aspect-video rounded-xl bg-[#0A1120] border-2 border-[#E11D48]/40 flex items-center justify-center mb-5 overflow-hidden">
              <ScanLine className="w-12 h-12 text-white/5" />
              <div className="absolute inset-3 border border-[#E11D48]/30 rounded-lg" />
              <div className="absolute top-2.5 left-2.5 font-mono text-[10px] text-[#FB7185] flex items-center gap-1.5">
                <AlertTriangle className="w-3 h-3" /> ATTENTION DRIFT DETECTED
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              {[{ l: "FOCUS SCORE", v: "86%" }, { l: "DRIFT EVENTS", v: "02" }, { l: "MODULE", v: "HR · 3/5" }].map((s) => (
                <div key={s.l} className="rounded-xl bg-white/5 border border-white/10 p-3">
                  <div className="font-mono text-[9px] text-white/35 mb-1 tracking-[0.08em]">{s.l}</div>
                  <div className="font-display font-bold text-sm">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}

// ─── Section: Final CTA ───────────────────────────────────────────────────────

function FinalCta() {
  return (
    <section className="relative overflow-hidden bg-[#0025D8] py-24 text-white text-center">
      <div
        className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[50rem] h-[50rem] rounded-full opacity-50"
        style={{ background: "radial-gradient(circle, rgba(124,58,237,0.35) 0%, transparent 60%)" }}
      />
      <div className="relative max-w-2xl mx-auto px-6">
        <p className="font-mono text-[10px] text-white/60 tracking-[0.2em] uppercase mb-4">Get started today</p>
        <h2 className="font-display font-black text-5xl md:text-6xl tracking-tight mb-5 leading-[1.05]">
          Stop guessing.<br />Start hearing back.
        </h2>
        <p className="text-white/70 text-base mb-9">Free to start. No credit card. Your ATS score in under a minute.</p>
        <div className="flex gap-3 justify-center flex-wrap">
          <Link
            to="/signup?role=candidate"
            className="inline-flex items-center gap-2 bg-white text-[#0025D8] hover:bg-white/90 px-8 py-4 rounded-xl font-bold text-base shadow-xl transition-colors"
          >
            Start applying free <ArrowUpRight className="w-5 h-5" />
          </Link>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 border border-white/30 text-white hover:bg-white/10 px-8 py-4 rounded-xl font-semibold text-base transition-colors"
          >
            Log in
          </Link>
        </div>
      </div>
    </section>
  );
}

// ─── Section: Company banner (slim) ──────────────────────────────────────────

function CompanyBanner() {
  return (
    <section className="bg-white border-b border-border">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-7 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#0F172A]/8 flex items-center justify-center flex-shrink-0">
            <Building2 className="w-5 h-5 text-[#0F172A]" />
          </div>
          <div>
            <div className="font-semibold text-sm text-[#0F172A]">Hiring instead of job-hunting? Screen smarter. Hire better.</div>
            <div className="text-xs text-muted-foreground">AI screening from job post to ranked shortlist in minutes.</div>
          </div>
        </div>
        <Link
          to="/solutions/ai-screening"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0025D8] hover:gap-2.5 transition-all"
        >
          For companies <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
}

// ─── Section: Footer ──────────────────────────────────────────────────────────

function FooterCol({ title, children }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/40 mb-4">{title}</div>
      <ul className="space-y-2.5">{children}</ul>
    </div>
  );
}

function FooterLink({ to, children }) {
  return (
    <li>
      <Link to={to} className="text-sm text-white/60 hover:text-white transition-colors">{children}</Link>
    </li>
  );
}

function Footer() {
  return (
    <footer className="bg-[#0F172A] text-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-10 md:gap-8">
          {/* Brand */}
          <div className="col-span-2">
            <LogoCandidateDark className="h-9 w-auto mb-5" />
            <p className="text-sm text-white/50 leading-relaxed max-w-xs">
              AI-powered interview practice for candidates — and AI screening for the teams that hire them.
            </p>
            <div className="flex items-center gap-2.5 mt-6">
              <a
                href="https://www.linkedin.com/company/vetrahub/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="VetraHub on LinkedIn"
                className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-white/10 bg-white/5 text-white/60 hover:text-white hover:bg-white/10 hover:border-white/20 transition-colors"
              >
                <Linkedin className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Features */}
          <FooterCol title="Features">
            {FEATURE_LINKS.map((f) => (
              f.comingSoon ? (
                <li key={f.key} className="flex items-center gap-2 text-sm text-white/35 select-none">
                  {f.label}
                  <span className="font-mono text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/10 text-white/50">Soon</span>
                </li>
              ) : (
                <FooterLink key={f.key} to={f.to}>{f.label}</FooterLink>
              )
            ))}
          </FooterCol>

          {/* For Employers */}
          <FooterCol title="For Employers">
            {SOLUTION_LINKS.map((f) => (
              <FooterLink key={f.key} to={f.to}>{f.label}</FooterLink>
            ))}
          </FooterCol>

          {/* Get started */}
          <div className="col-span-2 md:col-span-1">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/40 mb-4">Get started</div>
            <ul className="space-y-2.5">
              <FooterLink to="/signup?role=candidate">Sign up free</FooterLink>
              <FooterLink to="/signup?role=company">For companies</FooterLink>
              <FooterLink to="/book-demo">Book a demo</FooterLink>
              <FooterLink to="/login">Log in</FooterLink>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-14 pt-6 border-t border-white/10 flex items-center justify-between flex-wrap gap-3">
          <span className="font-mono text-[11px] text-white/40">© 2026 VetraHub. All rights reserved.</span>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="font-mono text-[11px] text-white/40 hover:text-white/70 transition-colors">Privacy</Link>
            <span className="font-mono text-[11px] text-white/30">Practice smarter. Land the offer.</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────

export default function Landing() {
  return (
    <main className="bg-white text-foreground">
      <Hero />
      <StatsStrip />
      <Modules />
      {/* Autopilot first: it is what the hero now promises, so the first thing a scrolling
          visitor meets should be that promise expanded, not a different feature. */}
      <Autopilot />
      <ResumeAnalyzer />
      <AiCoaching />
      <DeliveryAndIntegrity />
      <FinalCta />
      <CompanyBanner />
      <Footer />
    </main>
  );
}
